import { useDisclosure } from '@mantine/hooks';
import clsx from 'clsx';
import { isEmpty } from 'lodash-es';
import { useMemo } from 'react';
import { Content } from 'src/contexts/ContentContext';
import { useMultiSelectionContext } from 'src/contexts/MultiSelectionContext';
import { useSiteTheme } from 'src/contexts/SiteTheme/SiteThemeContext';
import { vars } from 'src/core/themes';
import { Stack, Tooltip } from 'src/core/ui';
import { LaunchBroadcastListing } from 'src/modals/ListingDetails/actions/LaunchBroadcastListing';
import { LightingBoltIcon, SeatSaverIcon } from 'src/svgs';
import { GenericDisclosuresIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { Event, Listing, ListingNote } from 'src/WebApiController';

import * as styles from './MobileListingIconDisplay.css';

export const MobileListingAutoPricingEnabledDisplay = ({
  isAutoPricingEnabled,
}: {
  isAutoPricingEnabled?: boolean | null;
}) => {
  const { isDarkMode } = useSiteTheme();
  const [open, { toggle }] = useDisclosure(false);
  if (!isAutoPricingEnabled) {
    return null;
  }
  return (
    <Tooltip
      open={open}
      delayDuration={0}
      onOpenChange={toggle}
      contentVariant={isDarkMode ? 'light' : 'dark'}
      triggerContent={
        <div
          className={clsx(styles.iconContainer, styles.iconActiveBackground)}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            toggle();
          }}
        >
          <LightingBoltIcon
            fill={vars.color.textBrand}
            size={vars.iconSize.m}
          />
        </div>
      }
    >
      <Content id={ContentId.AutoPricingEnabled} />
    </Tooltip>
  );
};

export const MobileListingSeatSaverDisplay = ({
  isSeatSaver,
}: {
  isSeatSaver?: boolean | null;
}) => {
  if (!isSeatSaver) {
    return null;
  }
  return (
    <div className={styles.iconContainer}>
      <SeatSaverIcon fill={vars.color.textWarning} size={vars.iconSize.m} />
    </div>
  );
};

export const MobileListingNotesDisplay = ({
  listingNotes,
}: {
  listingNotes?: ListingNote[] | null;
}) => {
  const { isDarkMode } = useSiteTheme();
  const [open, { toggle }] = useDisclosure(false);

  if (isEmpty(listingNotes)) {
    return null;
  }

  return (
    <Tooltip
      open={open}
      delayDuration={0}
      contentVariant={isDarkMode ? 'light' : 'dark'}
      triggerContent={
        <div
          className={styles.iconContainer}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            toggle();
          }}
        >
          <GenericDisclosuresIcon
            fill={vars.color.textPrimary}
            size={vars.iconSize.m}
          />
        </div>
      }
      onOpenChange={toggle}
    >
      <Stack direction="column" style={{ padding: `${vars.spacing['sm']} 0` }}>
        {listingNotes?.map(({ listingNoteId, listingNoteText }) => (
          <span key={listingNoteId}>{`• ${listingNoteText}`}</span>
        ))}
      </Stack>
    </Tooltip>
  );
};

export const MobileListingBroadcastDisplay = ({
  listing,
  event,
}: {
  listing: Listing;
  event: Event;
}) => {
  const { getSelection } = useMultiSelectionContext();
  const listingSelection = useMemo(
    () => getSelection(event.virtId),
    [event.virtId, getSelection]
  );
  return (
    <div className={clsx(styles.iconContainer, styles.iconActiveBackground)}>
      <LaunchBroadcastListing
        forTable={true}
        event={event}
        listing={listing}
        lockListingGroupFromEdits={undefined}
        iconOnlyMode={true}
        disabled={
          listingSelection.isSingleGroupMultiSelect || !listing.availQty
        }
      />
    </div>
  );
};
